import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { array, date, object, string } from 'yup';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import QRCode from 'qrcode.react';
import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import flattenDeep from 'lodash/flattenDeep';
import { Column } from 'components/UI/Column';
import { Row } from 'components/UI/Row';
import { BottomActionBar } from 'components/UI/BottomActionBar';
import { DocumentsDialog } from 'components/UI/Dialog/DocumentsDialog';
import fileDownload from 'js-file-download';
import { axiosInstance } from 'features/axiosInstance';
import { DocLinkType } from 'routes/routes';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  approveOrderList,
  fetchOrderListDetails,
  getOrderListDetails,
  getOrdersListDetailsLoaders,
  getOrdersListDetailsApiErrors,
  resetDetails,
  OrderListProduct,
  OrderListSerialNumber,
  saveOrderList
} from './ordersListForm';
import { FormControl, InputLabel, MenuItem, OutlinedInput, Select } from '@material-ui/core';
import { PrintOrderListDialog } from 'components/UI/Dialog/PrintOrderListDialog';
import { ConfigureLabelForPrint } from 'features/PrintServices/ConfigureLabelForPrintDialog';

const FieldSchema = object().shape({
  product_id: string(),
  customer_item_code: string().required('orderList.customer item code required'),
  qa_type: string().required('orderList.qa type required')
});

const FormSchema = object().shape({
  product_data: array().of(FieldSchema),
  reference_code: string().required('orderList.reference code required'),
  customer_reference_code: string().required('orderList.customer reference code required'),
  received_at: date().required('orderList.received at date required')
});

export type FinalizeOrderListField = {
  customer_item_code: string;
  product_id: string;
  qa_type: string;
};

type FinalizeOrderListProps = {
  product_data: FinalizeOrderListField[];
  short_name: string;
  reference_code: string;
  customer_reference_code: string;
  received_at: moment.Moment;
};

const FinalizeOrderList: FC<{}> = ({ }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const orderList = useSelector(getOrderListDetails);
  const { orderListId } = useParams<{ orderListId: string }>();
  const { isSaving, isLoading } = useSelector(getOrdersListDetailsLoaders);
  const { loadingError } = useSelector(getOrdersListDetailsApiErrors);
  const [showDocsModal, setShowDocsModal] = useState<boolean>(false);
  const [groupedRows, setGroupedRows] = useState<any>();

  const [printDialogIsOpen, setPrintDialogIsOpen] = useState<boolean>(false);
  const [printLabelsDialogIsOpen, setPrintLabelsDialogIsOpen] = useState<boolean>(false);

  let preselectedSerialNumbers = Array<string>();

  if (orderList) {
    orderList.products.forEach(product => {
      product.serial_number.forEach(serialNumber => {
        preselectedSerialNumbers.push(serialNumber.value);
      });
    });
  }

  const printOrderList = async (isTestingDocument: boolean, includeCompanyDetails: boolean, printLang: string) => {
    const response: any = await axiosInstance.post(
      `/print/orderlist/${orderListId}`,
      { lang: printLang, is_testing_document: isTestingDocument, include_company_details: includeCompanyDetails },
      { responseType: 'blob' }
    );
    const { data } = response;
    fileDownload(data, `${orderList?.short_name ?? 'order-list-' + orderListId}.pdf`);
  };

  const { handleSubmit, errors, control, trigger, getValues } = useForm<FinalizeOrderListProps>({
    mode: 'onSubmit',
    resolver: yupResolver(FormSchema)
  });

  const serialNumbers = useMemo(() => flattenDeep(orderList?.products.map(p => p.serial_number.map(s => s.value))), [
    orderList
  ]);

  useEffect(() => {
    dispatch(fetchOrderListDetails(orderListId));
    return () => {
      dispatch(resetDetails());
    };
  }, []);

  useMemo(() => {
    setGroupedRows(groupBy(orderList?.products, 'id'));
  }, [orderList]);

  const onSubmit = useCallback(
    async (formData: any) => {
      const { reference_code, customer_reference_code, product_data, received_at } = formData;
      const result = await trigger();
      if (result && orderList) {
        dispatch(
          approveOrderList({
            id: orderList?.id,
            orderList: {
              serial_numbers: serialNumbers,
              reference_code,
              customer_reference_code,
              product_data,
              received_at,
              short_name: orderList?.short_name,
              tracking_url: orderList?.tracking_url
            }
          })
        );
      }
    },
    [dispatch, orderList]
  );

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  if (isLoading) {
    return (
      <Row justifyContent="center" alignItems="center">
        <CircularProgress color="primary" size={34} />
      </Row>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Column margin="0 0 50px 0">

        <Row margin="10px 0" justifyContent="center">
          {orderList?.created_at ? (
            <>
              {t('orderList.acquired on')} {moment(orderList?.created_at).format(t('_.date format'))}
            </>
          ) : (
            <></>
          )}
        </Row>

        <Row margin="10px 0" justifyContent="center">
          {orderList?.approved_at ? (
            <>
              {t('orderList.approved on')} {moment(orderList?.approved_at).format(t('_.date format'))}
            </>
          ) : (
            <>{t('orderList.draft')}</>
          )}
        </Row>

        <Row margin="10px 0 50px 0">
          <Controller
            as={
              <TextField
                variant="outlined"
                label={t('label.name')}
                fullWidth
              />
            }
            defaultValue={orderList?.short_name || ''}
            name={`short_name`}
            control={control}
          />
        </Row>

        <Row margin="10px 0">{t('orderList.reference codes')}</Row>
        <Row margin="10px 0">
          <Controller
            as={
              <TextField
                fullWidth
                variant="outlined"
                label={t('orderList.reference code')}
                helperText={t(get(errors, 'reference_code.message'))}
                error={!isEmpty(get(errors, 'reference_code'))}
              />
            }
            defaultValue={orderList?.reference_code || ''}
            name={`reference_code`}
            control={control}
          />
        </Row>
        <Row margin="10px 0">
          <Controller
            as={
              <TextField
                fullWidth
                variant="outlined"
                label={t('orderList.tracking url')}
                helperText={t(get(errors, 'tracking_url.message'))}
                error={!isEmpty(get(errors, 'tracking_url'))}
              />
            }
            defaultValue={orderList?.tracking_url || ''}
            name={`tracking_url`}
            control={control}
          />
        </Row>

        <Row margin="10px 0 50px 0">
          <Controller
            as={
              <TextField
                fullWidth
                variant="outlined"
                label={t('orderList.customer reference code')}
                helperText={t(get(errors, 'customer_reference_code.message'))}
                error={!isEmpty(get(errors, 'customer_reference_code'))}
              />
            }
            defaultValue={orderList?.customer_reference_code || ''}
            name={`customer_reference_code`}
            control={control}
          />
        </Row>

        <Row margin="10px 0">{t('orderList.reference dates')}</Row>
        <Row margin="10px 0 50px 0">
          <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={moment.locale()}>
            <Controller
              render={(props) => (
                <KeyboardDatePicker
                  autoOk
                  fullWidth
                  variant='inline'
                  inputVariant='outlined'
                  label={t('orderList.order received at')}
                  format={t('_.date format')}
                  maxDate={moment(orderList?.created_at)}
                  disableFuture
                  InputAdornmentProps={{ position: 'start' }}
                  invalidDateMessage={t('orderList.invalid date')}
                  maxDateMessage={t('orderList.future date not allowed', { futureDate: moment(orderList?.created_at).format(t('_.date format')) })}
                  {...props}
                />
              )}

              defaultValue={orderList?.received_at || orderList?.created_at || moment()}
              name={`received_at`}
              control={control}
            />
          </MuiPickersUtilsProvider>
        </Row>

        {!isEmpty(groupedRows) && (
          <>
            {/*  {map(groupedRows, (gr: any, productId: any) => { */}
            {Object.keys(groupedRows).map((key, index) => (
              <React.Fragment key={key}>
                <Row margin="10px 0 20px">{groupedRows[key][0].name}</Row>
                <Row justifyContent="space-between">
                  <Column flex={0.5}>
                    <Controller
                      as={
                        <TextField
                          fullWidth
                          autoComplete="new-password"
                          variant="outlined"
                          helperText={t(get(errors, `product_data[${index}].customer_item_code.message`))}
                          error={!isEmpty(get(errors, `product_data[${index}].customer_item_code`))}
                          style={{ marginBottom: '16px' }}
                          label={t('orderList.customer item code')}
                        />
                      }
                      control={control}
                      defaultValue={groupedRows[key][0].customer_item_code || ''}
                      name={`product_data[${index}].customer_item_code`}
                    />
                  </Column>
                  <Column>
                    <FormControl
                      variant="outlined"
                      error={!isEmpty(get(errors, `product_data[${index}].qa_type`))}
                      style={{ marginLeft: '16px', marginBottom: '16px' }}
                    >
                      <InputLabel id="qa-type-label">{t('orderList.qa report type')}</InputLabel>
                      <Controller
                        as={
                          <Select
                            disabled={!isEmpty(orderList?.approved_at)}
                            labelId="qa-type-label"
                            input={<OutlinedInput label={t('orderList.qa report type')} style={{ minWidth: '250px' }} />}
                          >
                            <MenuItem value="none">{t('orderList.qa.none')}</MenuItem>
                            <MenuItem value="partial">{t('orderList.qa.partial')}</MenuItem>
                            <MenuItem value="complete">{t('orderList.qa.complete')}</MenuItem>
                          </Select>
                        }
                        control={control}
                        defaultValue={groupedRows[key][0].qa_type || 'none'}
                        name={`product_data[${index}].qa_type`}
                      />
                    </FormControl>
                  </Column>
                </Row>

                <Controller
                  as={<input value={key} type="hidden" />}
                  control={control}
                  defaultValue={key}
                  name={`product_data[${index}].product_id`}
                />

                <TableContainer component={Paper} elevation={3}>
                  <Table aria-label="order list">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">POS</TableCell>
                        <TableCell align="center">{t('label.serial number')}</TableCell>
                        <TableCell align="center">{t('label.qr code')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedRows[key].map((op: OrderListProduct) => (
                        <React.Fragment key={op.id}>
                          {op.serial_number.map((os: OrderListSerialNumber, idx: number) => (
                            <TableRow key={os.value}>
                              <TableCell align="center">{idx + 1}</TableCell>
                              <TableCell align="center">{os.value}</TableCell>
                              <TableCell align="center">
                                <QRCode
                                  style={{ marginTop: '20px', marginBottom: '30px' }}
                                  value={os.value}
                                  renderAs="svg"
                                  size={50}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <div style={{ height: '50px' }} />
              </React.Fragment>
            ))}
          </>
        )}
      </Column>
      {!isLoading && (
        <BottomActionBar>
          <Row padding="20px" justifyContent="space-between">
            <Button
              variant="contained"
              color="primary"
              disabled={isSaving}
              style={{ width: '200px' }}
              onClick={() => setShowDocsModal(true)}
            >
              {t('label.documents')}
            </Button>

            <div>
              <Button
                onClick={() => {
                  let raw_received_at = getValues().received_at;
                  let received_at: any;

                  if (raw_received_at instanceof string) {
                    received_at = moment(raw_received_at).format('YYYY-MM-DD');
                  } else if (moment.isMoment(raw_received_at)) {
                    received_at = raw_received_at.format('YYYY-MM-DD');
                  }

                  dispatch(
                    saveOrderList({
                      id: orderList?.id,
                      orderList: { ...getValues(), received_at, serial_numbers: serialNumbers },
                      detailsOnly: true
                    })
                  );
                }}
                variant="contained"
                color="primary"
                style={{ marginRight: '20px', width: '200px' }}
              >
                {t('label.update details')}
              </Button>

              {!orderList?.approved_at && (
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isSaving}
                  style={{ width: '200px' }}
                  type="submit"
                >
                  {t('label.approve')}
                  {isSaving && (
                    <CircularProgress
                      color="primary"
                      size={34}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: -17,
                        marginLeft: -17
                      }}
                    />
                  )}
                </Button>
              )}

              {orderList?.approved_at && (
                <>
                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => setPrintDialogIsOpen(true)}
                    style={{ width: '200px' }}
                  >
                    {t('label.print')}&nbsp;
                    <FontAwesomeIcon icon={faPrint} />
                  </Button>

                  <Button
                    variant="contained"
                    color="inherit"
                    onClick={() => setPrintLabelsDialogIsOpen(true)}
                    style={{ marginLeft: '20px', width: '200px' }} 
                  >
                    {t('label.print labels')}&nbsp;
                    <FontAwesomeIcon icon={faPrint} />
                  </Button>
                </>
              )}
            </div>
          </Row>
        </BottomActionBar>
      )}
      <DocumentsDialog
        closeFn={() => setShowDocsModal(false)}
        docType={DocLinkType.ORDER_LIST}
        open={showDocsModal}
        resourceId={orderListId}
      />
      <PrintOrderListDialog
        closeFn={() => setPrintDialogIsOpen(false)}
        open={printDialogIsOpen}
        confirmFn={printOrderList}
      />
      <ConfigureLabelForPrint
        closeFn={() => { setPrintLabelsDialogIsOpen(false) }}
        isOpen={printLabelsDialogIsOpen}
        properties={[]}
        targets={preselectedSerialNumbers}
        completeFn={() => { setPrintLabelsDialogIsOpen(false) }}
      />
    </form>
  );
};

export { FinalizeOrderList };
